import { useEffect, useCallback, useRef, useState } from "react";
import {
  DetailTypeCommandCreated,
  DetailTypeCommandUpdated,
} from "@screencloud/native-players-helpers";
import { Notification } from "src/utils/notification";

import { useAppContext } from "../../hooks/useAppContext";
import {
  ScreenDeviceCommandData,
  useCreateScreenDeviceCommandMutation,
} from "../../types.g";
import { useRdmMessage } from "./RealtimeRdmProvider";
import { useDeviceRdmState } from "./useDeviceRdmState";
import { hasRdmCommands } from "./hasRdmCommands";
import {
  isQuietCommand,
  RDM_COMMAND_TYPE,
  type RdmCommandType,
} from "./rdmCommandType";

export { isQuietCommand, RDM_COMMAND_TYPE };
export type { RdmCommandType };

export const DEFAULT_RDM_COMMAND_WAIT_MS = 60_000;

export const SAFETY_COMMAND_MS = 120_000;

export type WaitForRdmCommandOptions = {
  timeoutMs?: number;
};

// Hold the loading spinner + defer the result toast briefly after a command
// to allow the deviceState refetch to complete before the result is shown.
export const DEFERRED_COMMAND_RESULT_MS = 1500;

export enum CommandStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

/** Transport COMPLETED can still carry `{ success: false }`. */
function isBusinessFailure(result: unknown): boolean {
  return (
    result != null &&
    typeof result === "object" &&
    "success" in result &&
    result["success"] === false
  );
}

function isCommandDone(
  status: string | null | undefined,
): status is
  | typeof CommandStatus.COMPLETED
  | typeof CommandStatus.FAILED
  | typeof CommandStatus.CANCELED {
  return (
    status === CommandStatus.COMPLETED ||
    status === CommandStatus.FAILED ||
    status === CommandStatus.CANCELED
  );
}

/** Resolve/reject waitForRdmCommand from final status + payload. */
function finishWait<T>(
  status: CommandStatus,
  result: unknown,
  resolve: (value: T) => void,
  reject: (error: Error) => void,
): void {
  if (status === CommandStatus.COMPLETED) {
    if (isBusinessFailure(result)) {
      reject(new Error("Command completed with failure"));
      return;
    }
    resolve(result as T);
    return;
  }
  if (status === CommandStatus.FAILED || status === CommandStatus.CANCELED) {
    reject(new Error(`Command ${status}`));
  }
}

type CommandWaiter<T> = {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

export function useRdmCommand(screenId: string) {
  const context = useAppContext();
  const {
    deviceState,
    lastCommand: wsLastCommand,
    isRdmOnline,
  } = useDeviceRdmState(screenId);
  const latestMessage = useRdmMessage();
  const [createScreenDeviceCommandMutation] =
    useCreateScreenDeviceCommandMutation();

  const isCommandSupported = useCallback(
    (...commands: RdmCommandType[]): boolean =>
      hasRdmCommands(deviceState?.apiSchema, ...commands),
    [deviceState?.apiSchema],
  );

  const [pendingCommand, setPendingCommand] =
    useState<ScreenDeviceCommandData | null>(null);

  // Loading in-flight command type: set synchronously on click so the button
  // spinner appears immediately, before the mutation resolves or WS reports PROCESSING.
  const [sendingCommandType, setSendingCommandType] =
    useState<RdmCommandType | null>(null);
  // After clearLoading / safety fire, ignore leftover WS PROCESSING until
  // the next send. Id matching is not enough when lastCommandIdRef is null.
  const [ignoreStuckProcessing, setIgnoreStuckProcessing] = useState(false);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitersRef = useRef(new Map<string, CommandWaiter<any>>());

  const clearSafetyTimer = (reason: string) => {
    if (!safetyTimerRef.current) return;
    console.debug("[RDM] Safety timer clear", { reason });
    clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = null;
  };

  const clearLoading = useCallback(() => {
    setSendingCommandType(null);
    setIgnoreStuckProcessing(true);
    clearSafetyTimer("clearLoading");
  }, []);

  const cancelDeferred = useCallback(() => {
    if (deferredTimerRef.current) {
      clearTimeout(deferredTimerRef.current);
      deferredTimerRef.current = null;
    }
  }, []);

  const rejectAllWaiters = useCallback((reason: string) => {
    const waiters = Array.from(waitersRef.current.values());
    waitersRef.current.clear();
    for (const waiter of waiters) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error(reason));
    }
  }, []);

  const defer = useCallback(
    (fn: () => void) => {
      cancelDeferred();
      deferredTimerRef.current = setTimeout(() => {
        deferredTimerRef.current = null;
        fn();
      }, DEFERRED_COMMAND_RESULT_MS);
    },
    [cancelDeferred],
  );

  // Use `function` (not arrow) so `<T>` is a generic in .tsx, not JSX.
  // Prettier also strips `<T,>` trailing commas, which breaks arrow generics.
  const waitForRdmCommand = useCallback(
    function waitForRdmCommand<T>(
      commandId: string,
      options?: WaitForRdmCommandOptions,
    ): Promise<T> {
      const timeoutMs = options?.timeoutMs ?? DEFAULT_RDM_COMMAND_WAIT_MS;

      return new Promise<T>((resolve, reject) => {
        const existing = waitersRef.current.get(commandId);
        if (existing) {
          clearTimeout(existing.timer);
          existing.reject(new Error("Replaced by a newer wait"));
          waitersRef.current.delete(commandId);
        }

        const timer = setTimeout(() => {
          waitersRef.current.delete(commandId);
          clearLoading();
          reject(new Error("Command timed out"));
        }, timeoutMs);

        waitersRef.current.set(commandId, {
          // Waiters are keyed by commandId; callers cast via <T>.
          resolve: resolve as (value: T) => void,
          reject,
          timer,
        });
      });
    },
    [clearLoading],
  );

  useEffect(() => {
    setPendingCommand(null);
    setSendingCommandType(null);
    setIgnoreStuckProcessing(false);
    clearSafetyTimer("screen-changed");
    cancelDeferred();
    rejectAllWaiters("Screen changed");
  }, [screenId, cancelDeferred, rejectAllWaiters]);

  useEffect(
    () => () => {
      clearSafetyTimer("unmount");
      if (deferredTimerRef.current) clearTimeout(deferredTimerRef.current);
      rejectAllWaiters("Unmounted");
    },
    [rejectAllWaiters],
  );

  useEffect(() => {
    const msg = latestMessage;
    if (!msg?.commandId) return;
    if (
      msg["detail-type"] !== DetailTypeCommandUpdated &&
      msg["detail-type"] !== DetailTypeCommandCreated
    ) {
      return;
    }

    const waiter = waitersRef.current.get(msg.commandId);
    if (!waiter) return;

    if (isCommandDone(msg.status)) {
      clearTimeout(waiter.timer);
      waitersRef.current.delete(msg.commandId);
      finishWait(msg.status, msg.result, waiter.resolve, waiter.reject);
    }
  }, [latestMessage]);

  const lastCommand =
    wsLastCommand?.commandId === pendingCommand?.commandId
      ? wsLastCommand
      : (wsLastCommand ?? pendingCommand);

  const lastCommandIdRef = useRef<string | null>(null);
  lastCommandIdRef.current = lastCommand?.commandId ?? null;

  const wsProcessing =
    lastCommand?.status === CommandStatus.PROCESSING && !ignoreStuckProcessing;

  // TODO: Add translation mapping from RDM `commandType` (e.g. device.clear_cache) to
  // human-readable labels and include them in Notification messages (sent / completed / failed).

  const lastNotifiedRef = useRef<string | null>(null);

  const notifyCommandResult = useCallback(
    (
      status: string | null,
      commandType: string | null,
      commandId: string | null,
      result?: unknown,
    ) => {
      if (isQuietCommand(commandType)) return;

      const dedupeKey = `${commandId ?? "unknown"}:${status}`;
      if (lastNotifiedRef.current === dedupeKey) return;
      lastNotifiedRef.current = dedupeKey;

      if (status === CommandStatus.COMPLETED) {
        // Business failure still arrives as transport COMPLETED; callers show
        // their own error toast — skip the generic success toast.
        if (isBusinessFailure(result)) return;

        Notification.success({
          message: context.intl.formatMessage({
            id: "screens.notification.rdm.completed",
            defaultMessage: "Command completed successfully",
          }),
        });
      } else {
        Notification.error({
          message: context.intl.formatMessage({
            id: "screens.notification.rdm.failed",
            defaultMessage: "Command failed",
          }),
        });
      }
    },
    [context.intl],
  );

  const sendRdmCommand = useCallback(
    async (commandType: RdmCommandType, payload?: Record<string, unknown>) => {
      if (!isRdmOnline) {
        return null;
      }

      // Show the spinner immediately on click.
      setSendingCommandType(commandType);
      setIgnoreStuckProcessing(false);
      // Drop any stale finished command
      setPendingCommand(null);
      // Cancel a pending deferred clear/toast from a previous command if it exists
      cancelDeferred();
      // (Re)start the safety timer
      clearSafetyTimer("restart");
      console.debug("[RDM] Safety timer start", {
        commandType,
        ms: SAFETY_COMMAND_MS,
      });
      safetyTimerRef.current = setTimeout(() => {
        console.debug("[RDM] Safety timer triggered", {
          commandType,
          commandId: lastCommandIdRef.current,
        });
        clearLoading();
      }, SAFETY_COMMAND_MS);

      try {
        const result = await createScreenDeviceCommandMutation({
          variables: {
            input: {
              screenId,
              commandType,
              ...(payload !== undefined ? { payload } : {}),
            },
          },
        });

        const commandResult = result.data?.createScreenDeviceCommand;

        if (commandResult?.success === false) {
          clearLoading();
          Notification.error({
            message:
              commandResult.message ??
              context.intl.formatMessage({
                id: "screens.notification.rdm.failed",
                defaultMessage: "Command failed",
              }),
          });
          return null;
        }

        if (commandResult?.data) {
          setPendingCommand(commandResult.data as ScreenDeviceCommandData);
        } else {
          // No commandId will ever arrive for this send; don't hang the spinner.
          clearLoading();
        }

        if (commandResult != null && !isQuietCommand(commandType)) {
          Notification.success({
            message: context.intl.formatMessage({
              id: "screens.notification.rdm.sent",
              defaultMessage: "Command sent",
            }),
          });
        }

        return commandResult?.data?.commandId ?? null;
      } catch (e: unknown) {
        clearLoading();
        const message =
          e instanceof Error
            ? e.message
            : context.intl.formatMessage({
                id: "screens.notification.rdm.failed",
                defaultMessage: "Command failed",
              });
        Notification.error({ message });
        return null;
      }
    },
    [
      isRdmOnline,
      cancelDeferred,
      clearLoading,
      context.intl,
      createScreenDeviceCommandMutation,
      screenId,
    ],
  );

  const sendRdmCommandAndWait = useCallback(
    async function sendRdmCommandAndWait<T>(
      commandType: RdmCommandType,
      payload?: Record<string, unknown>,
      options?: WaitForRdmCommandOptions,
    ): Promise<T> {
      const commandId = await sendRdmCommand(commandType, payload);
      if (!commandId) {
        throw new Error("Failed to create device command");
      }
      return waitForRdmCommand<T>(commandId, options);
    },
    [sendRdmCommand, waitForRdmCommand],
  );

  // When THIS send's command finishes (COMPLETED/FAILED), defer clearing the
  // spinner and firing the result toast
  useEffect(() => {
    if (!lastCommand?.status) return;
    if (lastCommand.commandId !== pendingCommand?.commandId) return;

    if (lastCommand.status === CommandStatus.PROCESSING) {
      console.debug("[RDM] Command processing", {
        commandId: lastCommand.commandId,
        commandType: lastCommand.commandType,
      });
      return;
    }

    // Only COMPLETED/FAILED/CANCELED carry a result to show. PENDING (the
    // create state) and any other in-progress status keep the spinner and fire
    // no toast — otherwise a command that is still running would show a
    // premature "Command failed".
    if (
      lastCommand.status !== CommandStatus.COMPLETED &&
      lastCommand.status !== CommandStatus.FAILED &&
      lastCommand.status !== CommandStatus.CANCELED
    ) {
      return;
    }

    const status = lastCommand.status;
    const commandType = lastCommand.commandType;
    const commandId = lastCommand.commandId;
    const result = lastCommand.result;

    defer(() => {
      clearLoading();
      notifyCommandResult(status, commandType, commandId, result);
    });
  }, [
    lastCommand?.status,
    lastCommand?.commandId,
    lastCommand?.commandType,
    lastCommand?.result,
    pendingCommand?.commandId,
    defer,
    clearLoading,
    notifyCommandResult,
  ]);

  const isCommandProcessing = useCallback(
    (commandType: RdmCommandType): boolean =>
      sendingCommandType === commandType ||
      (lastCommand?.commandType === commandType && wsProcessing),
    [sendingCommandType, lastCommand?.commandType, wsProcessing],
  );

  const isAnyCommandProcessing = sendingCommandType !== null || wsProcessing;

  return {
    isRdmOnline,
    isCommandSupported,
    lastCommand,
    sendRdmCommand,
    sendRdmCommandAndWait,
    waitForRdmCommand,
    isCommandProcessing,
    isAnyCommandProcessing,
    deviceState,
  };
}
